import { Box, Link } from '@hover/blueprint';
import type { GraphQLError } from 'graphql';
import React, { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { animated } from 'react-spring';
import type { InferType } from 'yup';

import { Header } from 'src/modules/onboarding/components/Login/Header';
import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import { useOnboardingError } from 'src/modules/onboarding/hooks/useOnboardingError';
import {
  makeEmailValidationSchema,
  makeSingleSignOnValidationSchema,
} from 'src/modules/onboarding/validations/makeValidationSchemas';
import { Form as SharedForm } from 'src/shared/components/forms';
import { useMagicLinkSigninMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useAuthenticationMethod,
  useEffectOnMount,
  useQuery,
  useSegment,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';
import { useSlide } from 'src/shared/transitions';

import { FormFields } from './FormFields';

type SupportedAuthenticationMethods = 'password' | 'sso';

const AnimatedHeader = animated(Header);

export const isSsoError = (errors: readonly GraphQLError[]) =>
  errors.some(error => error.message.match(/SSO strict mode/gi));

export const getActiveMethod = (
  pathname: string,
): SupportedAuthenticationMethods => {
  switch (pathname) {
    case `${Routes.SIGN_UP_ONBOARDING.path}/login`:
      return 'password';
    case `${Routes.SIGN_UP_ONBOARDING.path}/login/sso`:
      return 'sso';
    default:
      return 'password';
  }
};

export type EmailFormData = InferType<
  ReturnType<typeof makeEmailValidationSchema>
>;

const UsePassword = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { search } = useLocation();
  const [_state, dispatch] = useContext(OnboardingContext);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { getValues } = useFormContext<Record<string, string>>();

  const onClick = () => {
    const { email } = getValues();
    dispatch({ email });
    buttonPressed({
      button_text: 'Use password instead',
      page_or_screen_name: 'Login-email_input',
      feature: 'onboarding',
    });
    history.push({
      pathname: `${Routes.SIGN_UP_ONBOARDING.path}/password`,
      search,
    });
  };

  return (
    <Link
      alignSelf="center"
      as={Box}
      color="neutral.500"
      mt={600}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
      }}
      textDecoration="underline"
    >
      {t('onboarding.usePasswordInstead')}
    </Link>
  );
};

export const LoginComponent = () => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const { pathname, search } = useLocation();
  const history = useHistory();
  const query = useQuery();

  const { logError } = useOnboardingError();
  const { identify } = useSegment();
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

  const [magicLinkSignin] = useMagicLinkSigninMutation();
  const [isSSOEmail, setIsSSOEmail] = useState<boolean>(false);
  const [isStrictSSO, setIsStrictSSO] = useState<boolean>(false);
  const { enabledMethod, signInUrl, checkEmail } = useAuthenticationMethod();

  const activeAuthenticationMethod = getActiveMethod(pathname);

  let validationSchema;

  if (activeAuthenticationMethod === 'sso') {
    validationSchema = makeSingleSignOnValidationSchema(t, checkEmail);
  } else {
    validationSchema = makeEmailValidationSchema(t);
  }

  const [state, dispatch] = useContext(OnboardingContext);
  const initialValues = { email: state?.email ?? '' };

  const transitions = useSlide(activeAuthenticationMethod);

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Login-email_input',
      feature: 'onboarding',
    });
  });

  useEffect(() => {
    if (enabledMethod === 'sso') {
      history.push(
        `${Routes.SIGN_UP_ONBOARDING.path}/login/sso${
          new URL(window.location.href).search
        }`,
      );
      pageViewed({
        page_or_screen_name: 'Email-input_sso',
        feature: 'onboarding',
      });
    }
  }, [history, enabledMethod]);

  const copy: Record<
    SupportedAuthenticationMethods,
    { path: string; title?: string; heading: string; subheading: string }
  > = {
    sso: {
      path: `${Routes.SIGN_UP_ONBOARDING.path}/login/sso`,
      heading: t('onboarding.loginWithSSO'),
      subheading: 'Please log in with your Single Sign On credentials',
    },
    password: {
      path: `${Routes.SIGN_UP_ONBOARDING.path}/login`,
      heading: t('onboarding.enterYourEmail'),
      subheading: t('onboarding.wellEmailYouMagicLink'),
    },
  };

  const loginViaSSO = async ({
    email,
    ssoUrl,
  }: {
    email: string;
    ssoUrl: string;
  }) => {
    try {
      await identify({ email });
      window.location.href = ssoUrl;
    } catch (error) {
      logError(error as Error);
      setIsAuthenticating(false);
    }
  };

  const loginViaMagicLink = async (email: string) => {
    const redirectToUrl = query.get('redirect_to');

    const { data } = await magicLinkSignin({
      variables: {
        email,
        redirectToUrl,
      },
      onError: e => {
        setIsAuthenticating(false);
        if (isSsoError(e.graphQLErrors)) {
          setIsStrictSSO(true);
        } else {
          logError(e);
        }
      },
    });

    if (data?.magicLinkSignin) {
      setIsAuthenticating(false);
      history.push({
        pathname: `${Routes.SIGN_UP_ONBOARDING.path}/check`,
        search,
      });
    }
  };

  const onSubmit = ({ email }: EmailFormData) => {
    if (!email) return;

    setIsAuthenticating(true);
    dispatch({ email });

    if (isSSOEmail && signInUrl) {
      void loginViaSSO({ email, ssoUrl: signInUrl });
    } else if (activeAuthenticationMethod === 'password') {
      void loginViaMagicLink(email);
    } else {
      setIsAuthenticating(false);
    }
  };

  return (
    <Box alignItems="center" height="100vh" justifyContent="center">
      <Box flexDirection="column" position="absolute">
        {transitions((style, item) => {
          const { heading, subheading } = copy[item];

          return (
            <AnimatedHeader
              heading={heading}
              key={item}
              style={style}
              subheading={subheading}
              whiteSpace="pre"
            />
          );
        })}

        <Box flexDirection="column" mt={200}>
          <SharedForm<EmailFormData>
            display="flex"
            height="100%"
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            <FormFields
              isAuthenticating={isAuthenticating}
              setIsSSOEmail={setIsSSOEmail}
              setIsStrictSSO={setIsStrictSSO}
            />
            {!isStrictSSO && <UsePassword />}
          </SharedForm>
        </Box>
      </Box>
    </Box>
  );
};
